import React, {useEffect, useState} from 'react';
import {IGlobalStorefrontSettings, IStorefrontSettings} from '../../models/Storefront';
import {Dropdown, IDropdownOption} from '@fluentui/react';
import ToggleField from "../Common/ToggleField";
import {useGetVendorsQuery} from "../../store/Api";
import {useGetOrganizationQuery} from "../../store/endpoints/OrganizationsEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import NumberField from "../Inputs/NumberField";
import MarkupTypeSelection from "./MarkupTypeSelection";
import useCan, {PRODUCT_PROMOS_ADMIN} from "../../permissions/ability";

interface IStorefrontSettingsFormProps {
    globalSettings?: IGlobalStorefrontSettings;
    settings: IStorefrontSettings;
    onChange: (settings: IStorefrontSettings) => void;
    onLoading?: (isLoading: boolean) => void;
}

const StorefrontProductSettingsForm: React.FC<IStorefrontSettingsFormProps> = (props) => {
    // UX gate only. The permission is itself stripped unless the org holds the promos entitlement, and
    // the API forces both toggles back off without it regardless of what gets sent.
    const canManagePromos = useCan(PRODUCT_PROMOS_ADMIN);
    const [defaultCatalog, setDefaultCatalog] = useState<string>();
    const [storefrontVendors, setStorefrontVendors] = React.useState<string[]>([]);
    const vendors = useGetVendorsQuery(defaultCatalog ?? "", {skip: !defaultCatalog})
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    useEffect(() => {
        if(organization.data) {
            setDefaultCatalog(organization.data.defaultCatalog);
        }
    },[organization.data]);

    useEffect(() => {
        if(props.settings.storefrontVendors) {
            setStorefrontVendors(props.settings.storefrontVendors ?? [])
        }
    }, [props.settings.storefrontVendors]);

    const onChangeVendors = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void => {
        if (item) {
            props.onChange({...props.settings,
                storefrontVendors: item.selected ? [...storefrontVendors, item.key as string] : storefrontVendors?.filter(key => key !== item.key) });
        }
    };

    const filterStrategyOptions = () => {
        return [
            {key: 'exclude', text: 'Exclude'},
            {key: 'include', text: 'Include'},
        ]
    }

    return (
        <div>
            <Dropdown
                placeholder={"Select Vendors"}
                multiSelect={true}
                label={"Storefront Vendor Filter"}
                selectedKeys={props.settings.storefrontVendors}
                options={vendors?.data?.items?.map(vendor => ({ key: vendor, text: vendor })) ?? []}
                onChange={onChangeVendors}
                disabled={vendors.isLoading || vendors.isFetching}
            />
            <Dropdown
                placeholder={"Select Filter Strategy"}
                multiSelect={false}
                label={"Vendor Filter Strategy"}
                selectedKey={props.settings.vendorFilterStrategy ?? "include"}
                options={filterStrategyOptions()}
                onChange={(e, v) => {props.onChange({...props.settings, vendorFilterStrategy: v?.key.toString()})}}
            />
            <ToggleField
                inline={true}
                label="Ignore Inherited Products"
                checked={props.settings.ignoreInheritedProducts}
                onChange={(e, checked) => {
                    props.onChange({...props.settings, ignoreInheritedProducts: checked});
                }}
            />
            <NumberField label='Default Markup' decimal={true} value={props.settings.defaultMarkup} onChange={(v) => props.onChange({...props.settings, defaultMarkup: v ?? undefined})} placeholder={props.globalSettings?.defaultMarkup?.toString() ?? 'Not Set'} />
            <MarkupTypeSelection value={props.settings.defaultMarkupType} onChange={(v) => props.onChange({...props.settings, defaultMarkupType: v ?? ''})} label='Markup Type' />

            {/* Promos sit with the markup settings because that is what they change: a promo replaces
                the markup above, or prices from a distributor's promotional cost instead of their
                normal one. Hidden without the permission, which is itself only granted while the org
                holds the promos entitlement — and the API forces both back off without it regardless. */}
            {canManagePromos && <>
                <ToggleField
                    inline={true}
                    label="Honor Vendor Promos"
                    // Separate from the MSP toggle on purpose: a distributor's promotional cost lowers
                    // the shopper price on its own, with no action from the MSP, so a storefront needs
                    // to be able to run its own promos while opting out of that.
                    toolTip="Pass a distributor's promotional cost through to the shopper at your usual markup. Prices move when the vendor runs a promotion, without any action from you."
                    checked={props.settings.enableVendorPromos}
                    onChange={(e, checked) => {
                        props.onChange({...props.settings, enableVendorPromos: checked});
                    }}
                />
                <ToggleField
                    inline={true}
                    label="Honor Product Promos"
                    toolTip="Apply promos you create on products, which reduce the price out of your own margin. The shopper always gets whichever price is lower, and promos never stack with a vendor promotion."
                    checked={props.settings.enableMspPromos}
                    onChange={(e, checked) => {
                        props.onChange({...props.settings, enableMspPromos: checked});
                    }}
                />
            </>}
        </div>
    );
}

export default StorefrontProductSettingsForm;